import React from 'react';
import { ChatTheme, Question } from '@/types/theme';
import { Button } from '@/components/ui/button';
import { debugLog } from '@/utils/browserLogger';

/**
 * Props for QuestionOptions component
 * Following OfferChat.ai TypeScript standards
 */
interface QuestionOptionsProps {
  question: Question;
  onOptionSelect: (value: string) => void;
  theme?: ChatTheme;
  isDisabled?: boolean;
  className?: string;
}

interface Option {
  label: string;
  value: string;
  id?: string; // Add optional id for unique keys
}

/**
 * Component to display question options as buttons or other UI elements
 * Follows OfferChat.ai component patterns
 */
export function QuestionOptions({
  question,
  onOptionSelect,
  theme,
  isDisabled = false,
  className = '',
}: QuestionOptionsProps) {
  // Debug logging for question options
  debugLog('QuestionOptions', 'Component rendering', {
    questionId: question.id,
    questionType: question.type,
    questionText: question.text?.substring(0, 30) + '...',
    hasOptions: !!question.options?.length,
    optionsCount: question.options?.length || 0,
    isDisabled,
    timestamp: new Date().toISOString()
  });

  // Only render for question types that have options
  if (!['select', 'boolean', 'button'].includes(question.type)) {
    debugLog('QuestionOptions', 'Not rendering - unsupported question type', { type: question.type });
    return null;
  }

  // Normalize options to ensure they have label and value properties
  let options: Option[] = [];

  // Process options based on their format
  if (question.options && Array.isArray(question.options)) {
    // If options exist and are in array format
    if (question.options.length > 0) {
      // Check if options are already in {label, value} format
      if (typeof question.options[0] === 'object' && question.options[0] !== null) {
        options = question.options.map((opt, index) => {
          // Handle case where opt is an object
          if (typeof opt === 'object' && opt !== null) {
            const label = typeof opt.label === 'string' ? opt.label :
                         typeof opt.value === 'string' ? opt.value : '';
            const value = typeof opt.value === 'string' ? opt.value :
                         typeof opt.label === 'string' ? opt.label : '';
            return {
              label,
              value,
              id: opt.id || `${question.id}-option-${index}` // Generate unique id if not provided
            };
          }
          // Handle case where opt is a string
          const strValue = String(opt);
          return {
            label: strValue,
            value: strValue,
            id: `${question.id}-option-${index}`
          };
        });
      }
      // If options are strings, convert to {label, value} format
      else if (typeof question.options[0] === 'string') {
        options = question.options.map((opt, index) => ({
          label: String(opt),
          value: String(opt),
          id: `${question.id}-option-${index}`
        }));
      }
    }
  }

  // For boolean questions, always use Yes/No options regardless of what's provided
  if (question.type === 'boolean') {
    options = [
      { label: 'Yes', value: 'yes', id: `${question.id}-yes` },
      { label: 'No', value: 'no', id: `${question.id}-no` }
    ];
  }
  // If no valid options were found for other types, provide defaults
  else if (options.length === 0) {
    if (question.type === 'button') {
      // Default options for button type if none provided
      options = [
        { label: 'Option 1', value: 'option1', id: `${question.id}-option-1` },
        { label: 'Option 2', value: 'option2', id: `${question.id}-option-2` },
        { label: 'Option 3', value: 'option3', id: `${question.id}-option-3` },
        { label: 'Option 4', value: 'option4', id: `${question.id}-option-4` }
      ];
    }
  }

  // Determine button style based on theme
  const getButtonStyle = (isOutlined: boolean = false) => {
    return {
      backgroundColor: isOutlined ? 'transparent' : theme?.primarycolor || '#497CFD',
      color: isOutlined ? theme?.primarycolor || '#497CFD' : theme?.buttontextcolor || '#FFFFFF',
      border: `1px solid ${theme?.primarycolor || '#497CFD'}`,
      borderRadius: theme?.borderradius ? `${theme.borderradius}px` : '0.5rem',
      padding: '0.75rem 1.25rem',
      margin: '0.25rem',
      cursor: isDisabled ? 'not-allowed' : 'pointer',
      opacity: isDisabled ? 0.6 : 1,
      transition: 'all 0.2s ease',
      fontFamily: theme?.fontfamily || 'inherit',
      fontSize: '0.95rem',
      fontWeight: 600,
      textAlign: 'center' as const,
      whiteSpace: 'normal' as const,
      overflow: 'visible',
      textOverflow: 'clip',
      minWidth: '120px',
      maxWidth: '100%',
      boxShadow: isOutlined ? 'none' : '0 2px 4px rgba(0, 0, 0, 0.1)',
      display: 'inline-block',
      lineHeight: '1.5',
      position: 'relative' as const,
      textDecoration: 'none',
      userSelect: 'none' as const,
    };
  };

  // Helper function to safely get display text
  const getDisplayText = (option: Option, index?: number): string => {
    if (option.label) return option.label;
    if (option.value) return option.value;
    if (question.type === 'boolean' && index !== undefined) {
      return index === 0 ? 'Yes' : 'No';
    }
    return 'Option';
  };

  // Add fade-in animation style
  const fadeInAnimation = {
    animation: 'fadeIn 0.5s ease-in-out',
    opacity: 1
  };

  // Add keyframes for fade-in animation
  React.useEffect(() => {
    // Check if the style already exists
    if (!document.getElementById('question-options-animation')) {
      const style = document.createElement('style');
      style.id = 'question-options-animation';
      style.innerHTML = `
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `;
      document.head.appendChild(style);

      debugLog('QuestionOptions', 'Added fade-in animation style');
    }
  }, []);

  // Log when options are about to be rendered
  debugLog('QuestionOptions', 'Rendering question options', {
    questionType: question.type,
    optionsCount: options.length,
    options: options.map(o => ({ label: o.label, value: o.value })),
    timestamp: new Date().toISOString()
  });

  // Render different UI based on question type
  switch (question.type) {
    case 'button':
      // Button-style options in a grid or flex layout
      return (
        <div
          className={`question-options button-options ${className}`}
          style={fadeInAnimation}
        >
          {options.map((option, idx) => (
            <button
              key={option.id || option.value}
              onClick={() => onOptionSelect(option.value)}
              disabled={isDisabled}
              style={{
                ...getButtonStyle(),
                minWidth: '120px',
                padding: '0.75rem 1.25rem',
                fontWeight: 'bold',
                display: 'inline-block',
                textAlign: 'center',
                animation: `fadeIn 0.5s ease-in-out ${idx * 0.1}s both`
              }}
              className="option-button"
            >
              {getDisplayText(option)}
            </button>
          ))}
        </div>
      );

    case 'boolean':
      // Yes/No buttons side by side
      return (
        <div
          className={`question-options boolean-options ${className}`}
          style={fadeInAnimation}
        >
          {options.map((option, index) => (
            <button
              key={option.id || option.value}
              onClick={() => onOptionSelect(option.value)}
              disabled={isDisabled}
              style={{
                ...getButtonStyle(index === 1),
                minWidth: '100px',
                padding: '0.75rem 1.25rem',
                fontWeight: 'bold',
                display: 'inline-block',
                textAlign: 'center',
                animation: `fadeIn 0.5s ease-in-out ${index * 0.1}s both`
              }}
              className="boolean-option"
            >
              {getDisplayText(option, index)}
            </button>
          ))}
        </div>
      );

    case 'select':
      // Select options as a vertical list of buttons
      return (
        <div
          className={`question-options select-options ${className}`}
          style={fadeInAnimation}
        >
          {options.map((option, idx) => (
            <button
              key={option.id || option.value}
              onClick={() => onOptionSelect(option.value)}
              disabled={isDisabled}
              style={{
                ...getButtonStyle(true),
                textAlign: 'left',
                padding: '0.75rem 1rem',
                borderRadius: theme?.borderradius ? `${theme.borderradius}px` : '0.5rem',
                backgroundColor: 'rgba(0, 0, 0, 0.03)',
                border: '1px solid rgba(0, 0, 0, 0.1)',
                color: theme?.bottextcolor || '#000000',
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                animation: `fadeIn 0.5s ease-in-out ${idx * 0.1}s both`
              }}
              className="select-option"
            >
              <span style={{ marginRight: 'auto' }}>{getDisplayText(option)}</span>
            </button>
          ))}
        </div>
      );

    default:
      return null;
  }
}

export default QuestionOptions;
